import { memo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CaretRight } from "@phosphor-icons/react";

/**
 * CommandPalette — Cmd+K global command / navigation overlay.
 *
 * Built on top of the project's existing cmdk Command primitive,
 * styled with TraceQC design tokens.
 *
 * Props:
 *   open          boolean — controlled open state
 *   onOpenChange  (open: boolean) => void
 *   groups        CommandGroup[]
 *     .heading    string — group section label
 *     .items      CommandItem[]
 *       .id       string — unique key
 *       .label    string — primary display text
 *       .description string — secondary line (filename, grade, etc.)
 *       .icon     Phosphor icon component
 *       .shortcut string[] — hint keys, e.g. ["G", "H"] or ["⌘", "K"]
 *       .onSelect () => void | string — callback or navigate-to path
 *   placeholder   string — search input placeholder
 *   emptyText     string — shown when no results match
 *
 * @example
 * // In your layout / App.js:
 * const { open, setOpen } = useCommandPalette();
 *
 * <CommandPalette
 *   open={open}
 *   onOpenChange={setOpen}
 *   groups={[
 *     {
 *       heading: "Navigation",
 *       items: [
 *         { id: "home",    label: "Dashboard",     icon: House,    shortcut: ["G", "H"], onSelect: "/" },
 *         { id: "admin",   label: "Admin Console", icon: Shield,   shortcut: ["G", "A"], onSelect: "/admin" },
 *         { id: "reports", label: "Accuracy",      icon: ChartBar, shortcut: ["G", "R"], onSelect: "/accuracy" },
 *       ],
 *     },
 *     {
 *       heading: "Recent Dossiers",
 *       items: recentDossiers.map(d => ({
 *         id: d.id,
 *         label: d.filename,
 *         description: `${d.material_grade} · ${d.final_status}`,
 *         icon: FilePdf,
 *         onSelect: `/dossier/${d.id}`,
 *       })),
 *     },
 *   ]}
 * />
 */

const CommandPalette = memo(function CommandPalette({
  open,
  onOpenChange,
  groups = [],
  placeholder = "Search or navigate…",
  emptyText = "No results found.",
}) {
  const navigate = useNavigate();

  const handleSelect = useCallback(
    (item) => {
      onOpenChange(false);
      if (typeof item.onSelect === "string") {
        navigate(item.onSelect);
      } else if (typeof item.onSelect === "function") {
        item.onSelect();
      }
    },
    [navigate, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-lg overflow-hidden border-zinc-200 shadow-xl rounded-sm">
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <DialogDescription className="sr-only">
          Search dossiers and pages, then press Enter to go there. Arrow keys move
          between results, Escape closes.
        </DialogDescription>
        <Command
          className={[
            "bg-white rounded-sm",
            "[&_[cmdk-group-heading]]:font-mono-data",
            "[&_[cmdk-group-heading]]:text-[10px]",
            "[&_[cmdk-group-heading]]:uppercase",
            "[&_[cmdk-group-heading]]:tracking-wider",
            "[&_[cmdk-group-heading]]:text-zinc-400",
            "[&_[cmdk-group-heading]]:px-3",
            "[&_[cmdk-group-heading]]:py-2",
          ].join(" ")}
        >
          {/* Input */}
          <div className="flex items-center border-b border-zinc-200 px-1">
            <CommandInput
              placeholder={placeholder}
              className="h-11 text-sm border-0 outline-none focus:ring-0 placeholder:text-zinc-400 bg-transparent flex-1"
            />
          </div>

          {/* Results */}
          <CommandList className="max-h-[380px] overflow-y-auto py-1">
            <CommandEmpty className="py-8 text-sm text-zinc-400 text-center font-mono-data">
              {emptyText}
            </CommandEmpty>

            {groups.map((group, gi) => (
              <span key={group.heading ?? gi}>
                {gi > 0 && <CommandSeparator className="bg-zinc-100 my-1" />}
                <CommandGroup heading={group.heading}>
                  {(group.items || []).map((item) => {
                    const Icon = item.icon;
                    return (
                      <CommandItem
                        key={item.id}
                        value={`${item.label} ${item.description ?? ""}`}
                        onSelect={() => handleSelect(item)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-sm cursor-pointer text-zinc-700 data-[selected=true]:bg-zinc-100 data-[selected=true]:text-zinc-900 transition-colors"
                      >
                        {Icon && (
                          <span
                            className="shrink-0 w-5 h-5 flex items-center justify-center rounded-sm bg-zinc-100 text-zinc-500"
                            aria-hidden="true"
                          >
                            <Icon size={12} weight="duotone" />
                          </span>
                        )}

                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium truncate">
                            {item.label}
                          </span>
                          {item.description && (
                            <span className="block text-[11px] text-zinc-400 font-mono-data truncate">
                              {item.description}
                            </span>
                          )}
                        </span>

                        {item.shortcut?.length > 0 && (
                          <span
                            className="flex items-center gap-0.5 shrink-0"
                            aria-hidden="true"
                          >
                            {item.shortcut.map((key, ki) => (
                              <kbd
                                key={ki}
                                className="font-mono-data text-[10px] bg-zinc-100 border border-zinc-200 px-1 py-0.5 rounded text-zinc-500 leading-none"
                              >
                                {key}
                              </kbd>
                            ))}
                          </span>
                        )}

                        <CaretRight
                          size={12}
                          className="text-zinc-300 shrink-0"
                          aria-hidden="true"
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </span>
            ))}
          </CommandList>

          {/* Footer */}
          <div className="border-t border-zinc-100 px-3 py-2 flex items-center gap-3">
            {[
              { key: "↑↓", label: "navigate" },
              { key: "↵",  label: "select" },
              { key: "esc", label: "close" },
            ].map(({ key, label }) => (
              <span key={key} className="font-mono-data text-[10px] text-zinc-400 flex items-center gap-1">
                <kbd className="bg-zinc-100 border border-zinc-200 px-1 py-0.5 rounded text-[9px] leading-none">
                  {key}
                </kbd>
                {label}
              </span>
            ))}
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
});

/**
 * useCommandPalette — manage open state and wire up Cmd+K / Ctrl+K shortcut.
 *
 * @returns {{ open: boolean, setOpen: fn, toggle: fn }}
 *
 * @example
 * const { open, setOpen } = useCommandPalette();
 * return <CommandPalette open={open} onOpenChange={setOpen} groups={groups} />;
 */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle]);

  return { open, setOpen, toggle };
}

export default CommandPalette;
